import { useCallback, useEffect, useState } from 'react';

export interface CallTask {
  task_id: string;
  createdAt: string;
  createdBy: string;
  status: 'Completed' | 'Pending' | 'Failed' | 'Draft';
  contactsCount: number;
  taskName: string;
  formData?: any;
  currentStep?: number;
}

const STORAGE_KEYS = {
  TASKS: 'batch_call_tasks',
  DRAFTS: 'batch_call_drafts',
};

export const useTaskStorage = () => {
  const [tasks, setTasks] = useState<CallTask[]>([]);
  const [drafts, setDrafts] = useState<CallTask[]>([]);

  useEffect(() => {
    const storedTasks = localStorage.getItem(STORAGE_KEYS.TASKS);
    const storedDrafts = localStorage.getItem(STORAGE_KEYS.DRAFTS);

    if (storedTasks) {
      setTasks(JSON.parse(storedTasks));
    }
    if (storedDrafts) {
      setDrafts(JSON.parse(storedDrafts));
    }
  }, []);

  const saveTask = useCallback(
    (task: CallTask) => {
      const updatedTasks = [...tasks, task];
      setTasks(updatedTasks);
      localStorage.setItem(STORAGE_KEYS.TASKS, JSON.stringify(updatedTasks));
    },
    [tasks]
  );

  const updateTask = useCallback(
    (task: CallTask) => {
      const updatedTasks = tasks.map(t =>
        t.task_id === task.task_id ? task : t
      );
      setTasks(updatedTasks);
      localStorage.setItem(STORAGE_KEYS.TASKS, JSON.stringify(updatedTasks));
    },
    [tasks]
  );

  const saveDraft = useCallback(
    (draft: CallTask) => {
      const updatedDrafts = [...drafts, draft];
      setDrafts(updatedDrafts);
      localStorage.setItem(STORAGE_KEYS.DRAFTS, JSON.stringify(updatedDrafts));
    },
    [drafts]
  );

  const updateDraft = useCallback(
    (draft: CallTask) => {
      const updatedDrafts = drafts.map(d =>
        d.task_id === draft.task_id ? draft : d
      );
      setDrafts(updatedDrafts);
      localStorage.setItem(STORAGE_KEYS.DRAFTS, JSON.stringify(updatedDrafts));
    },
    [drafts]
  );

  const removeDraft = useCallback(
    (taskId: string) => {
      const updatedDrafts = drafts.filter(draft => draft.task_id !== taskId);
      setDrafts(updatedDrafts);
      localStorage.setItem(STORAGE_KEYS.DRAFTS, JSON.stringify(updatedDrafts));
    },
    [drafts]
  );

  const getTaskById = useCallback(
    (taskId: string) => {
      return tasks.find(task => task.task_id === taskId);
    },
    [tasks]
  );

  const getDraftById = useCallback(
    (taskId: string) => {
      return drafts.find(draft => draft.task_id === taskId);
    },
    [drafts]
  );

  const removeTask = useCallback(
    (taskId: string) => {
      const updatedTasks = tasks.filter(task => task.task_id !== taskId);
      setTasks(updatedTasks);
      localStorage.setItem(STORAGE_KEYS.TASKS, JSON.stringify(updatedTasks));
    },
    [tasks]
  );

  return {
    tasks,
    drafts,
    saveTask,
    updateTask,
    saveDraft,
    updateDraft,
    removeDraft,
    getTaskById,
    getDraftById,
    removeTask,
  };
};
